import { z } from 'zod';
import { DISTRICTS } from './districts.js';
import type { PlanLimitsDto } from './plans.js';

/**
 * Phase 16c (ADR-0023 §11, 06 §28.11): subscriptions and payments in the bireysel edition. Prices are in kuruş, VAT
 * included (%20). The provider's own form takes the card; Oppy keeps the provider's references and the last four
 * digits only. The database's subscription is the one source of a person's access.
 */
export type BillingPeriod = 'monthly' | 'yearly';
export const BILLING_PERIODS = ['monthly', 'yearly'] as const;
export type BillingProviderId = 'fake' | 'iyzico';

/** A renewal not taken is tried again this many times, this many days apart, with a mail each time; then Ücretsiz */
export const BILLING_RETRIES = 3;
export const BILLING_RETRY_DAYS = 3;
/** A change of Pro reaches its subscribers this many days after their mail at least, from their next period */
export const PRO_CHANGE_NOTICE_DAYS = 30;
/** How long a checkout's hosted form stays open */
export const CHECKOUT_MINUTES = 30;

/** Pro's values a subscription has for a period: the prices of both periods and the limits */
export interface PlanTermsDto extends PlanLimitsDto {
  monthlyPriceKurus: number;
  yearlyPriceKurus: number;
  /** Phase 16c.1: the chat models the subscription has (ids) */
  chatModels: string[];
}

/** `GET /api/meta`'s `payments`: whether "Pro'ya geç" sells (a provider is configured), and whether it is a test one */
export interface PaymentsInfoDto {
  open: boolean;
  provider: BillingProviderId | null;
  /** the fake provider (development and tests; never in production): the form says so */
  test: boolean;
}

export const CANCEL_REASONS = ['price', 'little_use', 'missing_feature', 'other_tool', 'other'] as const;
export type CancelReason = (typeof CANCEL_REASONS)[number];

// Billing details ----------------------------------------------------------------------------------------------

/** Türkiye's 81 provinces, in Turkish alphabetical order (the İl select) */
export const PROVINCES = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya', 'Ankara', 'Antalya', 'Ardahan', 'Artvin', 'Aydın',
  'Balıkesir', 'Bartın', 'Batman', 'Bayburt', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa', 'Çanakkale',
  'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Düzce', 'Edirne', 'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir', 'Gaziantep',
  'Giresun', 'Gümüşhane', 'Hakkâri', 'Hatay', 'Iğdır', 'Isparta', 'İstanbul', 'İzmir', 'Kahramanmaraş', 'Karabük',
  'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kırıkkale', 'Kırklareli', 'Kırşehir', 'Kilis', 'Kocaeli', 'Konya', 'Kütahya',
  'Malatya', 'Manisa', 'Mardin', 'Mersin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye', 'Rize', 'Sakarya',
  'Samsun', 'Siirt', 'Sinop', 'Sivas', 'Şanlıurfa', 'Şırnak', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van',
  'Yalova', 'Yozgat', 'Zonguldak',
] as const;
export type Province = (typeof PROVINCES)[number];

/** 16c.1: a district of the province (the İlçe list, B2cOdeme) */
export function districtOf(province: string, district: string): boolean {
  return (DISTRICTS as Readonly<Record<string, readonly string[]>>)[province]?.includes(district) ?? false;
}

/** T.C. kimlik no: 11 digits, the first not 0, the 10th and 11th the official checksums */
export function validNationalId(value: string): boolean {
  if (!/^[1-9]\d{10}$/.test(value)) return false;
  const d = [...value].map(Number);
  const odd = d[0]! + d[2]! + d[4]! + d[6]! + d[8]!;
  const even = d[1]! + d[3]! + d[5]! + d[7]!;
  const tenth = (((odd * 7 - even) % 10) + 10) % 10;
  const eleventh = d.slice(0, 10).reduce((sum, n) => sum + n, 0) % 10;
  return d[9] === tenth && d[10] === eleventh;
}

/** Vergi kimlik no: 10 digits with the Revenue Administration's checksum */
export function validTaxNumber(value: string): boolean {
  if (!/^\d{10}$/.test(value)) return false;
  const d = [...value].map(Number);
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const tmp = (d[i]! + 9 - i) % 10;
    let v = (tmp * 2 ** (9 - i)) % 9;
    if (tmp !== 0 && v === 0) v = 9;
    sum += v;
  }
  return (10 - (sum % 10)) % 10 === d[9];
}

export interface BillingProfileDto {
  fullName: string;
  /** the national id's last two digits, when one is kept (the number itself never leaves the API) */
  nationalIdHint: string | null;
  province: string;
  district: string;
  address: string;
  company: boolean;
  companyName: string | null;
  taxNumber: string | null;
  taxOffice: string | null;
}

const text = (min: number, max: number) => z.string().trim().min(min).max(max);

export const billingProfileBody = z
  .object({
    fullName: text(2, 120),
    /** a new number; null: remove the kept one; left out: keep it as it is */
    nationalId: z
      .string()
      .trim()
      .refine(validNationalId, 'not a valid T.C. kimlik no')
      .nullable()
      .optional(),
    province: z.string().refine((p) => (PROVINCES as readonly string[]).includes(p), 'not a province'),
    district: text(2, 60),
    address: text(5, 300),
    company: z.boolean().default(false),
    companyName: text(2, 200).nullable().optional(),
    taxNumber: z.string().trim().refine(validTaxNumber, 'not a valid vergi kimlik no').nullable().optional(),
    taxOffice: text(2, 80).nullable().optional(),
  })
  .superRefine((value, ctx) => {
    // 16c.1: the İlçe is one of the İl's (a list on the page, B2cOdeme)
    if ((PROVINCES as readonly string[]).includes(value.province) && !districtOf(value.province, value.district)) {
      ctx.addIssue({ code: 'custom', path: ['district'], message: 'not a district of the province' });
    }
    if (!value.company) return;
    for (const field of ['companyName', 'taxNumber', 'taxOffice'] as const) {
      if (!value[field]) ctx.addIssue({ code: 'custom', path: [field], message: 'required for a company invoice' });
    }
  });
export type BillingProfileBody = z.infer<typeof billingProfileBody>;

// Checkout -------------------------------------------------------------------------------------------------------

/**
 * "Ödemeye geç": the period, the billing details, the two texts accepted (the versions the page showed; a page older
 * than the texts is refused) and "Pro'nun hemen başlamasını istiyorum" (no right of withdrawal).
 */
export const checkoutBody = z.object({
  period: z.enum(BILLING_PERIODS),
  billing: billingProfileBody,
  accept: z.object({
    preInformation: z.string().min(1).max(64),
    distanceSales: z.string().min(1).max(64),
    immediateStart: z.literal(true),
  }),
});
export type CheckoutBody = z.infer<typeof checkoutBody>;

/** The provider's own form, drawn in the checkout's dialog. The fake provider's is Oppy's test form. */
export interface HostedFormDto {
  kind: BillingProviderId;
  /** the form's session (the fake form sends it back; iyzico's token) */
  token: string;
  /** iyzico (16d): the form's script content, or a payment page address */
  content?: string;
  url?: string;
}

export interface CheckoutStartDto {
  orderId: string;
  form: HostedFormDto;
  period: BillingPeriod | null;
  amountKurus: number;
  vatKurus: number;
}

export interface CheckoutStatusDto {
  orderId: string;
  status: 'open' | 'paid' | 'failed' | 'expired';
  /** failed: the provider's reason, in a few words */
  failure: { code: string; message: string } | null;
  amountKurus: number;
  subscription: SubscriptionDto | null;
}

/** The fake provider's test form (never in production): which test card the "payment" uses */
export const FAKE_TEST_CARDS = ['success', 'decline', 'renewal_declines'] as const;
export type FakeTestCard = (typeof FAKE_TEST_CARDS)[number];
export const fakePayBody = z.object({ card: z.enum(FAKE_TEST_CARDS), cancel: z.boolean().optional() });

// The subscription -----------------------------------------------------------------------------------------------

export interface SubscriptionDto {
  id: string;
  period: BillingPeriod;
  /** the next renewal's period when it differs (monthly → yearly at the next renewal) */
  nextPeriod: BillingPeriod | null;
  status: 'active' | 'past_due' | 'ended';
  /** this period's price */
  priceKurus: number;
  /** what the next renewal takes (its period and, from their day, Pro's new values) */
  nextChargeKurus: number;
  startedAt: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  card: { last4: string; brand: string | null } | null;
  /** a renewal not taken: tries left and when the next is */
  retry: { retries: number; left: number; nextRetryAt: string | null } | null;
  terms: PlanTermsDto;
  /** a change of Pro this subscription was told about, and the period it starts */
  pendingTerms: { terms: PlanTermsDto; from: string } | null;
  endedAt: string | null;
}

export interface InvoiceDto {
  id: string;
  paymentId: string;
  /** when it was paid */
  date: string;
  description: string;
  amountKurus: number;
  vatKurus: number;
  paymentStatus: 'paid' | 'refunded';
  /** pending: "Hazırlanıyor · e-postayla gelecek"; issued: the number and the PDF */
  status: 'pending' | 'issued';
  number: string | null;
  hasPdf: boolean;
}

/** Ayarlar › Abonelik (`GET /api/billing`) */
export interface BillingOverviewDto {
  payments: PaymentsInfoDto;
  subscription: SubscriptionDto | null;
  profile: BillingProfileDto | null;
  invoices: InvoiceDto[];
  /** the last purchase's Ön Bilgilendirme Formu and Mesafeli Satış Sözleşmesi, as mailed */
  contracts: { orderId: string; date: string } | null;
}

export interface ContractsDto {
  orderId: string;
  date: string;
  preInformation: { title: string; version: string; text: string };
  distanceSales: { title: string; version: string; text: string };
}

export const cancelBody = z.object({ reason: z.enum(CANCEL_REASONS).nullable().optional() });
export const switchPeriodBody = z.object({ period: z.enum(BILLING_PERIODS) });

// Platform › Abonelikler ve ödemeler ------------------------------------------------------------------------------

export interface PlatformBillingSummaryDto {
  /** the month the tiles count (YYYY-MM, Europe/Istanbul) */
  month: string;
  active: number;
  monthly: number;
  yearly: number;
  takenKurus: number;
  takenCount: number;
  retrying: number;
  cancelled: number;
}

export interface PlatformPaymentRowDto {
  id: string;
  createdAt: string;
  paidAt: string | null;
  userId: string;
  name: string;
  /** "a***@gmail.com" */
  email: string;
  kind: 'first' | 'renewal';
  period: BillingPeriod;
  amountKurus: number;
  vatKurus: number;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  attempts: number;
  /** a failed payment no retry follows: the subscription went to Ücretsiz */
  final: boolean;
  failureText: string | null;
  /** the retries done after the renewal, and the next one's day */
  retries: number;
  nextRetryAt: string | null;
  providerRef: string | null;
  refundedAt: string | null;
  refundReason: string | null;
  invoice: { id: string; status: 'pending' | 'issued'; number: string | null; hasPdf: boolean } | null;
}

export interface PlatformPaymentsDto {
  rows: PlatformPaymentRowDto[];
  total: number;
  counts: { all: number; paid: number; failed: number; refunded: number };
  summary: PlatformBillingSummaryDto;
  page: number;
  size: number;
}

export const platformPaymentsQuery = z.object({
  status: z.enum(['paid', 'failed', 'refunded']).optional(),
  q: z.string().trim().max(120).optional(),
  /** YYYY-MM (Europe/Istanbul); empty: every month */
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional(),
  page: z.coerce.number().int().min(1).max(100_000).default(1),
  size: z.coerce.number().int().refine((v) => [10, 25, 50, 100].includes(v), 'one of 10, 25, 50, 100').default(25),
});

/** An invoice number as the accounting program gives it (e-Arşiv: "OPY2026000000412") */
export const invoiceNumberBody = z.object({ number: z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9-]{0,39}$/, 'letters, digits and dashes') });
export const refundBody = z.object({ reason: z.string().trim().min(3).max(300) });

/** Platform › Paketler's payment card: the provider and its connection ("bağlı değil" until 16d) */
export interface BillingProviderStatusDto {
  provider: BillingProviderId | null;
  connected: boolean;
  test: boolean;
  /** the last notification from the provider */
  lastEventAt: string | null;
  /** the provider's own words about the check, without keys */
  detail: string | null;
}

/** The invoice PDF the operator uploads: at most this big */
export const INVOICE_PDF_MAX_BYTES = 10 * 1024 * 1024;
